import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import { showNotification } from 'redux/actions/ui';
import { updateCustomerPushCartApi } from 'api';
import { calculateTotals } from '../utils/calculateTotals';
import { timelineValidationSchema } from './validation';
import TimelineDatePicker from './TimelineDatePicker';


const EditTimelineModal = ({
  open,
  setOpen,
  series
}) => {
  const dispatch = useDispatch();
  const {
    quotePlan,
    customerPushCart,
    currentQuoteCustomer
  } = useSelector((state: IReduxState) => state.quote);
  const { profile } = useSelector((state: IReduxState) => state.user);
  const activeStoreCode = profile?.stores?.[0].storeCode;
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const {
    _id,
    startDate,
    expirationDate,
    mustUsedDate,
    patientNotice,
    selectedItems,
    quoteDiscount: { price, price_type }
  } = series;

  const products = customerPushCart?.products ?
    Object.values(customerPushCart?.products).map(({product}) => product)
    : [];

  const {
    grandTotal
  } = calculateTotals(selectedItems, products);

  const handleSaveTimeline = async (values) => {
    const updatedQuotePlan = quotePlan.map((series) => {
      if (series._id === _id) {
        return {
          ...series,
          ...values
        };
      }

      return series;
    }).sort((a, b) => a.startDate - b.startDate);

    const updatedPushCart = {...customerPushCart, customer: currentQuoteCustomer, treatments: updatedQuotePlan };
    await updateCustomerPushCartApi({ dispatch, pushCartId: customerPushCart._id, pushCart: updatedPushCart });
    dispatch(showNotification('success', 'Timeline edited successfully'));
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="edit-timeline-dialog"
      PaperProps={{
        className: 'border-radius-6 p-3'
      }}
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle id="edit-timeline-dialog" className="flex-column w-full p-3">
        <div className="flex justify-between">
          <Typography variant="h5" className="uppercase">
            {'Edit Timeline'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <Formik
        initialValues={{
          startDate,
          expirationDate,
          mustUsedDate,
          patientNotice,
          quoteDiscount: { price, price_type }
        }}
        onSubmit={handleSaveTimeline}
        validationSchema={timelineValidationSchema(grandTotal)}
      >
        {({
          values: {
            startDate,
            expirationDate,
            mustUsedDate,
            patientNotice,
            quoteDiscount: { price, price_type }
          },
          errors,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue,
          handleChange
        }) => (
          <form onSubmit={handleSubmit} noValidate className="max-w-1000 mx-auto">
            <DialogContent className="px-8 pt-5 pb-10">
              <div className="font-bold text-12 mb-1">
                {'Timeline'}
              </div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TimelineDatePicker
                        label="Quote Start Date"
                        name="startDate"
                        value={startDate}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TimelineDatePicker
                        label="Quote Expiration"
                        name="expirationDate"
                        disabled={activeStoreCode === 'drnowak' && currentMonth === 10 && currentYear === 2025}
                        value={expirationDate}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TimelineDatePicker
                        label="Must Used Date"
                        name="mustUsedDate"
                        value={mustUsedDate || null}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        variant="outlined"
                        className="w-full"
                        name="patientNotice"
                        value={patientNotice}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Discount Type"
                        select
                        variant="outlined"
                        className="w-full"
                        name="quoteDiscount.price_type"
                        fullWidth
                        value={price_type}
                        onChange={(event) => {
                          const selectedValue = event.target.value;
                          const newPrice = selectedValue === '' ? null : price;
                          setFieldValue('quoteDiscount.price_type', selectedValue);
                          setFieldValue('quoteDiscount.price', newPrice);
                        }}
                        onBlur={handleBlur}
                        error={
                          (touched as {
                            quoteDiscount?: { price_type?: boolean };
                          })?.quoteDiscount?.price_type &&
                          Boolean(
                            (errors as {
                              quoteDiscount?: { price_type?: string };
                            })?.quoteDiscount?.price_type
                          )
                        }
                        helperText={
                          (touched as {
                            quoteDiscount?: { price_type?: boolean };
                          })?.quoteDiscount?.price_type &&
                          (errors as {
                            quoteDiscount?: { price_type?: string };
                          })?.quoteDiscount?.price_type
                        }
                      >
                        <MenuItem value="percent">Percentage</MenuItem>
                        <MenuItem value="fixed">Fixed</MenuItem>
                        <MenuItem value="">Not selected</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <NumberFormat
                        customInput={TextField}
                        variant="outlined"
                        name="quoteDiscount.price"
                        fullWidth
                        label="Discount Value"
                        value={price}
                        onValueChange={(values) => {
                          const { floatValue } = values;
                          handleChange({
                            target: {
                              name: 'quoteDiscount.price',
                              value: floatValue !== undefined ? floatValue : null
                            },
                          });
                        }}
                        onBlur={handleBlur}
                        disabled={!price_type}
                        suffix={price_type === 'percent' ? '%' : ''}
                        prefix={price_type === 'fixed' ? '$' : ''}
                        fixedDecimalScale
                        decimalScale={2}
                        error={(touched as { quoteDiscount?: { price?: boolean }})?.quoteDiscount?.price &&
                          Boolean(
                            (errors as {
                              quoteDiscount?: { price?: string };
                            })?.quoteDiscount?.price
                          )
                        }
                        helperText={
                          (touched as {
                            quoteDiscount?: { price?: boolean };
                          })?.quoteDiscount?.price &&
                          (errors as {
                            quoteDiscount?: { price?: string };
                          })?.quoteDiscount?.price
                        }
                      />
                    </Grid>
                  </Grid>
                </>
              </MuiPickersUtilsProvider>
            </DialogContent>
            <DialogActions className="justify-between">
              <Button
                variant="outlined"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditTimelineModal;
